import React, { useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { font } from "../../constants/font";
import ExecutionTracePanel from "./execution-trace-panel";
import { STATIC_PATH } from "../../constants/config";

const DRAWER_WIDTH = 520;

const DrawerBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? "visible" : "hidden")};
  transition: opacity 0.28s ease, visibility 0.28s ease;
  z-index: 1100;
`;

const DrawerShell = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${DRAWER_WIDTH}px;
  max-width: min(${DRAWER_WIDTH}px, 92vw);
  height: 100vh;
  background: #fafbfc;
  transform: translateX(${(props) => (props.isOpen ? "0" : "100%")});
  transition: transform 0.32s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1101;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: -12px 0 40px rgba(15, 23, 42, 0.18);

  @media screen and (max-width: 767px) {
    width: 100vw;
    max-width: 100vw;
  }
`;

const ReviewTab = styled.button`
  position: fixed;
  top: 50%;
  right: ${(props) => (props.isOpen ? `${props.drawerWidth}px` : "0")};
  z-index: 1102;
  transform: translateY(-50%);
  transition: right 0.32s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 40px;
  padding: 16px 8px;
  border: none;
  border-radius: 12px 0 0 12px;
  cursor: pointer;
  background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: -4px 0 16px rgba(102, 126, 234, 0.35);
  user-select: none;

  &:hover {
    filter: brightness(1.05);
  }

  &:focus-visible {
    outline: 2px solid #a5b4fc;
    outline-offset: 2px;
  }

  @media screen and (max-width: 767px) {
    right: ${(props) => (props.isOpen ? "100vw" : "0")};
    width: 36px;
    padding: 12px 6px;
  }
`;

const TabIcon = styled.img`
  width: 18px;
  height: 18px;
  filter: brightness(0) invert(1);
  transform: ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0)")};
  transition: transform 0.28s ease;
`;

const TabLabel = styled.span`
  font: ${font.bold_12};
  font-size: 11px;
  letter-spacing: 0.6px;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  line-height: 1.2;
  white-space: nowrap;
`;

const TabBadge = styled.span`
  font: ${font.bold_12};
  font-size: 9px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AgentReviewDrawer = ({
  isOpen,
  onToggle,
  onClose,
  executionTrace,
  projectTitle,
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!executionTrace) return null;

  const eventCount = executionTrace?.event_count ?? executionTrace?.events?.length ?? 0;

  return (
    <>
      <ReviewTab
        type="button"
        isOpen={isOpen}
        drawerWidth={DRAWER_WIDTH}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-label={isOpen ? "Close Agent Review" : "Open Agent Review"}
      >
        <TabIcon src={`${STATIC_PATH}sidebar-expand.svg`} alt="" isOpen={isOpen} />
        <TabLabel>Agent Review</TabLabel>
        {eventCount > 0 && (
          <TabBadge>{eventCount > 99 ? "99+" : eventCount}</TabBadge>
        )}
      </ReviewTab>

      <DrawerBackdrop isOpen={isOpen} onClick={onClose} aria-hidden={!isOpen} />

      <DrawerShell isOpen={isOpen} role="dialog" aria-modal="true" aria-label="Agent Review">
        <ExecutionTracePanel
          executionTrace={executionTrace}
          projectTitle={projectTitle}
          onClose={onClose}
          isDrawer
        />
      </DrawerShell>
    </>
  );
};

AgentReviewDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  executionTrace: PropTypes.object,
  projectTitle: PropTypes.string,
};

export default AgentReviewDrawer;
